import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Link, Typography, Fade } from "@mui/material";

import { IMAGES } from "@APP/assets";
import CONFIG from "@APP/config";
import { MOBILE_FONT, MOBILE_GUTTER } from "@APP/styles/mobile";

/**
 * Lloyds (LBG) variant of the app root screen.
 *
 * This screen is reached when someone opens the app without a payment request
 * link (the `*` route), so the approved design carries no journey chrome — no
 * header bar and no merchant avatar. It is just the Lloyds horse, a single
 * white notice card explaining how to get back into the journey, and the legal
 * links above the "Powered by Lloyds" mark.
 *
 * The store badges, registration link and version label of the base screen are
 * deliberately dropped: the LBG brand does not promote the OCM app here.
 */

// Lloyds-hosted legal pages take precedence over the payment provider's own
// (Moneyhub's), matching MoneyhubLegalInfo.
const LINKS = [
  {
    text: "Terms and conditions",
    href:
      CONFIG.URLS.LLOYDS?.TERMS_AND_CONDITIONS_URL ??
      CONFIG.URLS.PAYMENT_PROVIDER.TERMS_AND_CONDITIONS_URL,
    id: "homeScreenTermsAndConditionsLink",
  },
  {
    text: "Privacy policy",
    href: CONFIG.URLS.LLOYDS?.PRIVACY_URL ?? CONFIG.URLS.PAYMENT_PROVIDER.PRIVACY_POLICY_URL,
    id: "homeScreenPrivacyPolicyLink",
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    // Same 5vw page gutter as the journey screens (see PageLayout), so the
    // notice card lines up with the cards on the rest of the flow.
    padding: "24px 5vw",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      padding: `24px ${MOBILE_GUTTER}px`,
    },
  },
  // `margin: auto` rather than centring on the flex parent: auto margins
  // collapse to 0 once the content outgrows the space, so on a short viewport
  // the block scrolls from its top instead of being clipped.
  main: {
    margin: "auto",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 64,
    [theme.breakpoints.down("sm")]: {
      gap: 40,
    },
  },
  logo: {
    // The horse sits directly on the page background — appLogo.svg carries no
    // backing plate, so nothing is drawn behind it.
    width: 150,
    height: 150,
    objectFit: "contain",
    [theme.breakpoints.down("sm")]: {
      width: 110,
      height: 110,
    },
  },
  card: {
    width: "100%",
    // The design's card is noticeably rounder than the 10px theme radius.
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    // Deeper bottom padding: the copy sits above the optical centre of the card
    // in the design rather than being centred in it.
    padding: "32px 24px 48px",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 12,
      padding: "20px 16px 28px",
    },
  },
  notice: {
    fontSize: 16,
    [theme.breakpoints.down("sm")]: {
      fontSize: MOBILE_FONT.body,
    },
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    // The legal links and the "Powered by Lloyds" mark are two separate groups
    // in the design, hence the tight link gap and the larger gap below.
    gap: theme.spacing(0.5),
    paddingTop: 48,
    [theme.breakpoints.down("sm")]: {
      paddingTop: 32,
    },
  },
  legalLink: {
    fontSize: 16,
    [theme.breakpoints.down("sm")]: {
      fontSize: MOBILE_FONT.body,
    },
  },
  poweredBy: {
    marginTop: 32,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
  },
}));

const HomeScreen = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <Fade in timeout={700}>
      <Box component="main" className={styles.root} id="homeScreenContainer">
        {/* The brand name is carried by the horse mark, so the page heading is
            visually hidden but still announced. It stays outside the centred
            block below, whose `gap` would otherwise reserve space for it. */}
        <Typography component="h1" className="hidden" id="homeScreenProductName">
          {t("ProductName")}
        </Typography>
        <Box className={styles.main} id="homeScreenBox">
          <img src={IMAGES.APP_LOGO} className={styles.logo} alt="" id="homeScreenProductImage" />
          <Box className={styles.card} id="homeScreenNoticeCard">
            <Typography color="textPrimary" className={styles.notice} id="homeScreenNoticeText">
              {t("Screens.Home.returnToRequest")}
            </Typography>
          </Box>
        </Box>
        <Box component="footer" className={styles.footer} id="homeScreenFooter">
          {LINKS.map(({ text, href, id }) => (
            <Link
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              color="textPrimary"
              underline="always"
              className={styles.legalLink}
              id={id}>
              {text}
            </Link>
          ))}
          <Typography component="span" className={styles.poweredBy} id="homeScreenPoweredBy">
            Powered by Lloyds
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

export default HomeScreen;
